/**
 * Whether this client carries the development-only connection routes.
 *
 * Tailscale exposure and SSH Environments reach a machine through something on the machine the
 * client itself runs on, so the desktop bridge is the only place either can come from — and a
 * production build leaves those members out (see
 * `@afar-research/shared/developmentOnlyConnectionRoutes`). Asking the bridge what it carries is
 * therefore the whole question: a browser and a release answer it the same way, and neither needs
 * to know which of the two reasons applies.
 *
 * Read at the call site rather than remembered: the bridge is composed once, before the renderer
 * runs, so there is nothing to subscribe to.
 */
import { resolveApplicationReleaseChannel } from "@afar-research/shared/applicationRelease";

import { APP_VERSION } from "../branding";
import { isRuntimeWebBuild } from "../environments/primary/browserEntry.logic";

export function hasServerExposureBridge(): boolean {
  if (typeof window === "undefined") return false;
  return window.desktopBridge?.getServerExposureState !== undefined;
}

export function hasSshEnvironmentBridge(): boolean {
  if (typeof window === "undefined") return false;
  return window.desktopBridge?.ssh !== undefined;
}

/**
 * Whether this client is a development build, which also offers the maintainer routes — SSH and
 * Remote link — in the Add environment dialog. The Desktop answers for itself. A deployed Runtime
 * Web Client shows a Production Promotion's version rather than an Application Release's and is
 * never a development build. Any other browser is the bundle the Environment Service served, so
 * its own version and build mode decide.
 */
export function isDevelopmentReleaseChannel(): boolean {
  const desktopChannel =
    typeof window === "undefined" ? undefined : window.desktopBridge?.getReleaseChannel?.();
  const runtimeWeb = isRuntimeWebBuild({
    VITE_AFAR_RESEARCH_RUNTIME_WEB: import.meta.env.VITE_AFAR_RESEARCH_RUNTIME_WEB,
  });
  if (desktopChannel === undefined && !import.meta.env.DEV && runtimeWeb) return false;
  const channel =
    desktopChannel ??
    resolveApplicationReleaseChannel({
      version: APP_VERSION,
      isDevelopmentBuild: import.meta.env.DEV,
    });
  return channel === "development";
}
